import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Wallet, X } from 'lucide-react';
import * as cacheManager from '../utils/cacheManager';

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

const parseAmt = (val) => parseFloat(String(val || '').replace(/[^0-9.-]/g, '')) || 0;

const fmt = (num) =>
  (parseFloat(num) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const daysInMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};

// Days elapsed since the most recent Friday (0 = today is Friday)
const daysSinceLastFriday = (date) => {
  const d = new Date(date);
  // getDay(): 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  return (d.getDay() - 5 + 7) % 7;
};

// Next Friday strictly after date (if date is Friday, returns the following Friday)
const nextFridayDate = (date) => {
  const d = new Date(date);
  const daysUntil = ((5 - d.getDay() + 7) % 7) || 7;
  d.setDate(d.getDate() + daysUntil);
  return d;
};

const fmtDate = (date) =>
  new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Count Fridays strictly after startDate up to and including the 1st of next month
const countFridaysUntilNext1st = (date) => {
  const d = new Date(date);
  const next1st = new Date(d.getFullYear(), d.getMonth() + 1, 1, 12, 0, 0);
  let count = 0;
  const cur = new Date(d);
  cur.setDate(cur.getDate() + 1);
  while (cur <= next1st) {
    if (cur.getDay() === 5) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const ensureTrailingEmpty = (lines) => {
  const withContent = (lines || []).filter((l) => l.label || l.amount);
  return [...withContent, { label: '', amount: '' }];
};

// ---- LineSection ----
const LineSection = ({ title, accentClass, lines, section, onUpdate }) => {
  const total = (lines || []).reduce((s, l) => s + parseAmt(l.amount), 0);

  const handleChange = (idx, field, value) => {
    const next = [...lines];
    next[idx] = { ...next[idx], [field]: value };
    if (idx === lines.length - 1 && (next[idx].label || next[idx].amount)) {
      next.push({ label: '', amount: '' });
    }
    onUpdate(section, next);
  };

  const handleRemove = (idx) => {
    const next = lines.filter((_, i) => i !== idx);
    onUpdate(section, ensureTrailingEmpty(next));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold uppercase tracking-wider ${accentClass}`}>
          {title}
        </span>
        <span className="text-xs text-zinc-500 tabular-nums">${fmt(total)}</span>
      </div>
      {(lines || []).map((line, idx) => {
        const isLast = idx === lines.length - 1;
        return (
          <div key={idx} className="flex gap-2 items-center group">
            <input
              type="text"
              value={line.label}
              onChange={(e) => handleChange(idx, 'label', e.target.value)}
              placeholder="Label"
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 min-w-0 transition-colors"
            />
            <input
              type="number"
              value={line.amount === 0 && !line.label ? '' : line.amount}
              onChange={(e) => handleChange(idx, 'amount', e.target.value)}
              placeholder="0.00"
              className="w-28 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 text-right transition-colors tabular-nums"
            />
            {!isLast ? (
              <button
                onClick={() => handleRemove(idx)}
                className="text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div className="w-3.5 flex-shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---- BudgetBox ----
const BudgetBox = ({ snapshot, globalFirstAmount, onUpdate, onDelete }) => {
  const income = (snapshot.income || []).reduce((s, l) => s + parseAmt(l.amount), 0);
  const debt = (snapshot.debt || []).reduce((s, l) => s + parseAmt(l.amount), 0);
  const real = income - debt;

  const date = new Date(snapshot.date);
  const daysSinceFri = daysSinceLastFriday(date);
  const dayOfMonth = date.getDate();
  const dim = daysInMonth(date);
  const fridayAmt = parseAmt(snapshot.fridayAmount);
  const firstAmt = parseAmt(globalFirstAmount);

  const fridayPro = (daysSinceFri / 7) * fridayAmt;
  const firstPro = ((dayOfMonth - 1) / dim) * firstAmt;
  const relative = real - fridayPro - firstPro;

  const fridaysUntil1st = countFridaysUntilNext1st(date);
  const cashFor1st = firstAmt + fridaysUntil1st * fridayAmt;
  const nextFri = nextFridayDate(date);

  const toDateInputValue = () => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDateChange = (e) => {
    const [y, mo, d] = e.target.value.split('-').map(Number);
    const newDate = new Date(y, mo - 1, d, 12, 0, 0);
    onUpdate({ date: newDate.toISOString() });
  };

  const handleLineUpdate = (section, lines) => {
    onUpdate({ [section]: lines });
  };

  return (
    <div className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700/60">
        <input
          type="date"
          value={toDateInputValue()}
          onChange={handleDateChange}
          className="bg-transparent text-zinc-200 text-sm font-semibold focus:outline-none cursor-pointer [color-scheme:dark] hover:text-zinc-100 transition-colors"
        />
        <button
          onClick={onDelete}
          className="text-zinc-600 hover:text-red-400 transition-colors p-1 rounded"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* Cash */}
        <LineSection
          title="Cash"
          accentClass="text-emerald-400"
          lines={snapshot.income}
          section="income"
          onUpdate={handleLineUpdate}
        />

        {/* Debt */}
        <LineSection
          title="Debt"
          accentClass="text-red-400"
          lines={snapshot.debt}
          section="debt"
          onUpdate={handleLineUpdate}
        />

        {/* Upcoming payouts (read-only computed) */}
        <div className="space-y-2 pt-2 border-t border-zinc-700/60">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400">
              Cash needed · Fri {fmtDate(nextFri)}
            </span>
            <span className="text-sm tabular-nums text-zinc-300">${fmt(fridayAmt)}</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-400">Cash needed · next 1st</span>
              <div className="text-[10px] text-zinc-600 mt-0.5">
                ${fmt(firstAmt)} + {fridaysUntil1st} Friday{fridaysUntil1st !== 1 ? 's' : ''} (${fmt(fridaysUntil1st * fridayAmt)})
              </div>
            </div>
            <span className="text-sm tabular-nums text-zinc-300">${fmt(cashFor1st)}</span>
          </div>
        </div>

        {/* Summary */}
        <div className="space-y-2.5 pt-2 border-t border-zinc-700/60">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-400">Real balance</span>
              <div className="text-[10px] text-zinc-600 mt-0.5">
                Cash ${fmt(income)} − Debt ${fmt(debt)}
              </div>
            </div>
            <span
              className={`text-sm font-semibold tabular-nums ${
                real >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              ${fmt(real)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-400">Relative balance</span>
              <div className="text-[10px] text-zinc-600 mt-0.5 space-x-1.5">
                <span>Fri −${fmt(fridayPro)} ({daysSinceFri}/7)</span>
                <span>·</span>
                <span>1st −${fmt(firstPro)} ({dayOfMonth - 1}/{dim})</span>
              </div>
            </div>
            <span
              className={`text-sm font-bold tabular-nums ${
                relative >= 0 ? 'text-violet-400' : 'text-red-400'
              }`}
            >
              ${fmt(relative)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Budget ----
const Budget = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFridayAmount, setCurrentFridayAmount] = useState(0);
  const [globalFirstAmount, setGlobalFirstAmount] = useState(0);
  const [creating, setCreating] = useState(false);
  const saveTimers = useRef({});
  const settingsSaveTimer = useRef(null);

  // ---- Data fetching ----
  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/budget`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setSnapshots(
          data.map((s) => ({
            ...s,
            income: ensureTrailingEmpty(s.income),
            debt: ensureTrailingEmpty(s.debt),
          }))
        );
      }
    } catch (e) {
      console.error('[Budget] fetchSnapshots error:', e);
    }
    setLoading(false);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/budget/settings`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setGlobalFirstAmount(data.firstAmount || 0);
      }
    } catch (e) {
      console.error('[Budget] fetchSettings error:', e);
    }
  }, []);

  const fetchFridayAmount = useCallback(async () => {
    const weekKey = cacheManager.getWeekKey(0);
    const cached = cacheManager.getPayoutSlots(weekKey);
    if (cached) {
      const total = [...(cached.cleaners || []), ...(cached.owners || [])].reduce(
        (s, slot) => s + (slot.totalPayout || 0),
        0
      );
      setCurrentFridayAmount(total);
      return;
    }
    try {
      const weekStart = cacheManager.getWeekStart(0);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/payout-slots?weekStart=${weekStart.toISOString()}`,
        { headers: authHeader() }
      );
      if (res.ok) {
        const data = await res.json();
        cacheManager.setPayoutSlots(weekKey, data);
        const total = [...(data.cleaners || []), ...(data.owners || [])].reduce(
          (s, slot) => s + (slot.totalPayout || 0),
          0
        );
        setCurrentFridayAmount(total);
      }
    } catch (e) {
      console.error('[Budget] fetchFridayAmount error:', e);
    }
  }, []);

  useEffect(() => {
    fetchSnapshots();
    fetchSettings();
    fetchFridayAmount();
  }, [fetchSnapshots, fetchSettings, fetchFridayAmount]);

  // ---- Auto-save snapshots (debounced) ----
  const scheduleSave = useCallback((snapshot) => {
    const id = snapshot._id;
    if (!id) return;
    if (saveTimers.current[id]) clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(async () => {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/budget/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            date: snapshot.date,
            income: (snapshot.income || []).filter((l) => l.label || parseAmt(l.amount)),
            debt: (snapshot.debt || []).filter((l) => l.label || parseAmt(l.amount)),
            fridayAmount: parseAmt(snapshot.fridayAmount),
          }),
        });
      } catch (e) {
        console.error('[Budget] auto-save error:', e);
      }
    }, 800);
  }, []);

  // ---- Auto-save global settings (debounced) ----
  const scheduleSettingsSave = useCallback((amount) => {
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    settingsSaveTimer.current = setTimeout(async () => {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/budget/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ firstAmount: parseAmt(amount) }),
        });
      } catch (e) {
        console.error('[Budget] settings save error:', e);
      }
    }, 800);
  }, []);

  const handleFirstAmountChange = (value) => {
    setGlobalFirstAmount(value);
    scheduleSettingsSave(value);
  };

  // ---- Snapshot state updates ----
  const handleUpdate = useCallback(
    (id, updates) => {
      setSnapshots((prev) =>
        prev.map((s) => {
          if (s._id !== id) return s;
          const updated = { ...s, ...updates };
          if (updates.income !== undefined) updated.income = ensureTrailingEmpty(updates.income);
          if (updates.debt !== undefined) updated.debt = ensureTrailingEmpty(updates.debt);
          scheduleSave(updated);
          return updated;
        })
      );
    },
    [scheduleSave]
  );

  const handleCreate = async () => {
    setCreating(true);
    try {
      const today = new Date();
      const latest = snapshots[0];
      const copiedIncome = latest
        ? latest.income.filter((l) => l.label).map((l) => ({ label: l.label, amount: '' }))
        : [];
      const copiedDebt = latest
        ? latest.debt.filter((l) => l.label).map((l) => ({ label: l.label, amount: '' }))
        : [];
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/budget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          date: today.toISOString(),
          income: copiedIncome,
          debt: copiedDebt,
          fridayAmount: currentFridayAmount,
        }),
      });
      if (res.ok) {
        const created = await res.json();
        setSnapshots((prev) => [
          {
            ...created,
            income: ensureTrailingEmpty(created.income),
            debt: ensureTrailingEmpty(created.debt),
          },
          ...prev,
        ]);
      }
    } catch (e) {
      console.error('[Budget] create error:', e);
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/budget/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      setSnapshots((prev) => prev.filter((s) => s._id !== id));
    } catch (e) {
      console.error('[Budget] delete error:', e);
    }
  };

  // ---- Render ----
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Wallet className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-semibold text-zinc-100">Budget</h2>
          {currentFridayAmount > 0 && (
            <span className="text-xs text-zinc-500 ml-1">
              This Friday:{' '}
              <span className="text-zinc-300 tabular-nums">${fmt(currentFridayAmount)}</span>
            </span>
          )}
        </div>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Box
        </button>
      </div>

      {/* Global setting */}
      <div className="flex items-center gap-3 bg-zinc-800/40 border border-zinc-700/60 rounded-xl px-4 py-3 max-w-lg">
        <span className="text-sm text-zinc-300 flex-1">Money due at 1st</span>
        <input
          type="number"
          value={globalFirstAmount || ''}
          onChange={(e) => handleFirstAmountChange(e.target.value)}
          placeholder="0.00"
          className="w-32 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-violet-500 text-right tabular-nums transition-colors"
        />
      </div>

      {/* Boxes */}
      {snapshots.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <Wallet className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm">No budget boxes yet.</p>
          <p className="text-xs mt-1 text-zinc-600">Click "New Box" to get started.</p>
        </div>
      ) : (
        <div className="space-y-4 max-w-lg">
          {snapshots.map((snapshot) => (
            <BudgetBox
              key={snapshot._id}
              snapshot={snapshot}
              globalFirstAmount={globalFirstAmount}
              onUpdate={(updates) => handleUpdate(snapshot._id, updates)}
              onDelete={() => handleDelete(snapshot._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Budget;
